import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFirebaseAuthAxios } from "@/lib/firebase/authAxios";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { customerShopService } from "./customer-shop.service";
import type { APIResponse } from "@/core/types/api";
import type {
  AddToCartDto,
  CheckoutDto,
  CustomerCart,
} from "../types/customer-shop";
import {
  addLocalCustomerCartItem,
  removeLocalCustomerCartItem,
  updateLocalCustomerCartItem,
} from "../cart/customer-cart-storage";
import { syncLocalCustomerCartToServer } from "@/features/shop/cart/customer-cart-sync";

const api = createFirebaseAuthAxios();
const shopApi = customerShopService(api);

const emptyCartResponse = {
  success: true,
  data: null as unknown as CustomerCart,
};

const clearedCartResponse: APIResponse<{ cleared: true }> = {
  success: true,
  data: { cleared: true },
};

export function useAddToCustomerCart() {
  const { userAccount, loading } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AddToCartDto & { shopId: string }) => {
      if (!loading && userAccount?.role === "customer") {
        return shopApi.addToCart(data);
      }

      addLocalCustomerCartItem({
        shopId: data.shopId,
        productId: data.productId,
        quantity: data.quantity,
      });

      return emptyCartResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-cart"] });
    },
  });
}

export function useUpdateCustomerCartItem() {
  const { userAccount, loading } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      quantity,
    }: {
      productId: string;
      quantity: number;
    }) =>
      loading || userAccount?.role !== "customer"
        ? Promise.resolve({
            success: true,
            data: null as unknown as CustomerCart,
          })
        : shopApi.updateCartItem(productId, { quantity }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-cart"] });
    },
  });
}

/**
 * Sets a cart line item to an absolute quantity, for the guest/local cart or
 * the signed-in customer's server cart alike. Used by the +/- stepper and
 * number input on product cards once a product is already in the cart.
 */
export function useSetCustomerCartItemQuantity() {
  const { userAccount, loading } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      quantity,
    }: {
      productId: string;
      quantity: number;
    }) => {
      if (!loading && userAccount?.role === "customer") {
        return quantity > 0
          ? shopApi.updateCartItem(productId, { quantity })
          : shopApi.removeCartItem(productId);
      }

      if (quantity > 0) {
        updateLocalCustomerCartItem(productId, quantity);
      } else {
        removeLocalCustomerCartItem(productId);
      }

      return emptyCartResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-cart"] });
    },
  });
}

export function useRemoveCustomerCartItem() {
  const { userAccount, loading } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      loading || userAccount?.role !== "customer"
        ? Promise.resolve({
            success: true,
            data: null as unknown as CustomerCart,
          })
        : shopApi.removeCartItem(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-cart"] });
    },
  });
}

export function useClearCustomerCart() {
  const { userAccount, loading } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      loading || userAccount?.role !== "customer"
        ? Promise.resolve(clearedCartResponse)
        : shopApi.clearCart(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-cart"] });
    },
  });
}

export function useSyncLocalCustomerCart() {
  const { userAccount, loading } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      syncLocalCustomerCartToServer({ loading, role: userAccount?.role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-cart"] });
      queryClient.invalidateQueries({ queryKey: ["customer-orders"] });
      queryClient.invalidateQueries({ queryKey: ["customer-marketplace"] });
      queryClient.invalidateQueries({ queryKey: ["customer-shop"] });
    },
  });
}

export function useCheckoutCustomerOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CheckoutDto) => shopApi.checkout(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-cart"] });
      queryClient.invalidateQueries({ queryKey: ["customer-orders"] });
      queryClient.invalidateQueries({ queryKey: ["customer-marketplace"] });
      queryClient.invalidateQueries({ queryKey: ["customer-shop"] });
    },
  });
}
